/*
 * Levels Beyond CONFIDENTIAL
 *
 * Copyright 2003 - 2020 Levels Beyond Incorporated
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the
 * property of Levels Beyond Incorporated and its suppliers, if
 * any. The intellectual and technical concepts contained herein
 * are proprietary to Levels Beyond Incorporated and its suppliers
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this
 * material is unlawful and strictly forbidden unless prior
 * written permission is obtained from Levels Beyond Incorporated.
 */

/**
 * Created by seaster on 7/17/15.
 */
'use strict';
angular.module('prism-ui.components.data').service('PermissionsService', function (
    $q,
    loginService,
    PermissionsResourceFactory,
    UsersResourceFactory
) {
    var self = this;
    var APPROVAL_VIEW_STATE = 'PACKAGE_APPROVE_REJECT_VIEW_STATE';
    var SYSTEM_OPS_STATE = 'DEBUG_SYSTEM_OPERATIONS_VIEW_STATE';
    var SYSTEM_OPS_ONLY_STATES = [
        SYSTEM_OPS_STATE,
        'PACKAGE_BATCH_MODIFY_METADATA_VIEW_STATE',
        'CLIP_ACTION_BATCH_MODIFY_METADATA_VIEW_STATE'
    ];
    var currentUserPromise = null;
    var _permissionsMap = {};
    var systemOpsPromise = null;
    self.NO_PERMISSION = 0;
    self.EXECUTE_PERMISSION = 1;
    self.WRITE_PERMISSION = 2;
    self.READ_PERMISSION = 4;

    self.isAdmin = () => {
        return loginService.isAdmin();
    };

    // loginService provides some basic information on the user: name, isAdmin, when jwt expires
    // This function provides the rest of the login information
    self.getCurrentUser = () => {
        if (!currentUserPromise) {
            currentUserPromise = UsersResourceFactory.getCurrentUser().$promise
                .catch(function () {
                    currentUserPromise = null;
                    return null;
                });
        }
        return currentUserPromise;
    };

    self.isExternalUser = () => {
        return self.getCurrentUser().then(function (user) {
            return !!(user && user.externalUser);
        });
    };

    self.isSystemOperator = () => {
        if (!systemOpsPromise) {
            systemOpsPromise = self.canWrite(SYSTEM_OPS_STATE);
        }
        return systemOpsPromise;
    };

    self.clearPermissions = function () {
        _permissionsMap = {};
        currentUserPromise = null;
        systemOpsPromise = null;
    };

    self.getPermissionsByParentKey = function (parentKey) {
        var deferred = $q.defer();

        PermissionsResourceFactory.get({viewState: parentKey}).$promise.then(function (permissions) {
            var parentPermissions = {};
            angular.forEach(permissions, function (value) {
                _permissionsMap[value.viewState] = value;
                parentPermissions[value.viewState] = _permissionsMap[value.viewState];
            });
            deferred.resolve(parentPermissions);
        }, function (rejectReason) {
            deferred.reject(rejectReason);
        });

        return deferred.promise;
    };

    self.getPermissionsForViewState = function (viewStateKey) {
        var deferredCheck = $q.defer();
        if (_permissionsMap[viewStateKey]) {
            deferredCheck.resolve(_permissionsMap[viewStateKey]);
        } else if (SYSTEM_OPS_ONLY_STATES.indexOf(viewStateKey) === -1) {
            if (self.isAdmin()) {
                _permissionsMap[viewStateKey] = {};
                _permissionsMap[viewStateKey].viewState = viewStateKey;
                _permissionsMap[viewStateKey].permission = 7;
            }
            deferredCheck.resolve(_permissionsMap[viewStateKey]);
        } else if (viewStateKey !== SYSTEM_OPS_STATE) {
            self.isSystemOperator().then(function (isSystemOps) {
                return isSystemOps;
            }, function () {
                return false;
            }).then(function (isSystemOps) {
                _permissionsMap[viewStateKey] = {};
                _permissionsMap[viewStateKey].viewState = viewStateKey;
                _permissionsMap[viewStateKey].permission = (isSystemOps) ? 7 : 0;
                deferredCheck.resolve(_permissionsMap[viewStateKey]);
            });
        } else {
            deferredCheck.resolve(null);
        }

        return deferredCheck.promise.then(function (prevPermission) {
            if (prevPermission) {
                return prevPermission;
            }
            return PermissionsResourceFactory.get({viewState: viewStateKey}).$promise.then(function (permissions) {
                angular.forEach(permissions, function (value) {
                    _permissionsMap[value.viewState] = value;
                });
                return _permissionsMap[viewStateKey];
            });
        });
    };

    self.getReadWritePermissions = function (viewStateKey) {
        var readPromise = self.canRead(viewStateKey);
        var writePromise = self.canWrite(viewStateKey);

        return $q.all({read: readPromise, write: writePromise});
    };

    self.hasReadPermission = function (permission) {
        return (permission & self.READ_PERMISSION) === self.READ_PERMISSION;
    };

    self.canRead = function (viewStateKey) {
        var deferred = $q.defer();

        self.getPermissionsForViewState(viewStateKey).then(function (permissions) {
            deferred.resolve(self.hasReadPermission(permissions.permission));
        }, function (rejectReason) {
            deferred.reject(rejectReason);
        });

        return deferred.promise;
    };

    self.hasWritePermission = function (permission) {
        return (permission & self.WRITE_PERMISSION) === self.WRITE_PERMISSION;
    };

    self.canWrite = function (viewStateKey) {
        var deferred = $q.defer();

        self.getPermissionsForViewState(viewStateKey).then(function (permissions) {
            deferred.resolve(self.hasWritePermission(permissions.permission));
        }, function (rejectReason) {
            deferred.reject(rejectReason);
        });

        return deferred.promise;
    };

    self.hasExecutePermission = function (permission) {
        return (permission & self.EXECUTE_PERMISSION) === self.EXECUTE_PERMISSION;
    };

    self.canExecute = function (viewStateKey) {
        var deferred = $q.defer();

        self.getPermissionsForViewState(viewStateKey).then(function (permissions) {
            deferred.resolve(self.hasExecutePermission(permissions.permission));
        }, function (rejectReason) {
            deferred.reject(rejectReason);
        });

        return deferred.promise;
    };

    self.hasException = function (viewStateKey) {
        var deferred = $q.defer();

        self.getPermissionsForViewState(viewStateKey).then(function (permission) {
            deferred.resolve((permission.hasOwnProperty('availableAt') && permission.availableAt) ? permission.availableAt : null);
        }, function (rejectReason) {
            deferred.reject(rejectReason);
        });

        return deferred.promise;
    };

    self.exceptionApplies = function (viewStateKey) {
        var deferred = $q.defer();

        self.hasException(viewStateKey).then(function (exception) {
            if (exception) {
                var now = new Date(),
                    exceptionTime = new Date(exception);
                deferred.resolve(now <= exceptionTime);
            } else {
                deferred.resolve(false);
            }
        }, function (rejectReason) {
            deferred.reject(rejectReason);
        });

        return deferred.promise;
    };

    self.hasReadWritePermission = function (permission) {
        var mask = self.READ_PERMISSION | self.WRITE_PERMISSION;
        return (permission & mask) === mask;
    };

    self.canReadWrite = function (viewStateKey) {
        var deferred = $q.defer();

        self.getPermissionsForViewState(viewStateKey).then(function (permissions) {
            deferred.resolve(self.hasReadWritePermission(permissions.permission));
        }, function (rejectReason) {
            deferred.reject(rejectReason);
        });

        return deferred.promise;
    };

    self.hasReadWriteExecutePermission = function (permission) {
        var mask = self.READ_PERMISSION | self.WRITE_PERMISSION | self.EXECUTE_PERMISSION;
        return (permission & mask) === mask;
    };

    self.canReadWriteExecute = function (viewStateKey) {
        var deferred = $q.defer();

        self.getPermissionsForViewState(viewStateKey).then(function (permissions) {
            deferred.resolve(self.hasReadWriteExecutePermission(permissions.permission));
        }, function (rejectReason) {
            deferred.reject(rejectReason);
        });

        return deferred.promise;
    };

    self.hasWriteExecutePermission = function (permission) {
        var mask = self.WRITE_PERMISSION | self.EXECUTE_PERMISSION;
        return (permission & mask) === mask;
    };

    self.canWriteExecute = function (viewStateKey) {
        return self.getPermissionsForViewState(viewStateKey).then(function (permissions) {
            return $q.when(self.hasWriteExecutePermission(permissions.permission));
        });
    };

    self.getPackagePermissions = function (viewStateKey, pkgId) {
        return PermissionsResourceFactory.get({viewState: viewStateKey, packageId: pkgId}).$promise;
    };

    self.packageExceptionApplies = function (viewState) {
        if(viewState[0] &&  viewState[0].hasOwnProperty('availableAt') && viewState[0].availableAt) {
            var now = new Date(),
            exceptionTime = new Date(viewState[0].availableAt);
            return viewState[0].viewState === APPROVAL_VIEW_STATE ? true : now.getTime() <= exceptionTime.getTime();
        } else {
            return false;
        }
    };

    self.canWritePackage = function (viewStateKey, pkgId) {
        return self.getPackagePermissions(viewStateKey, pkgId).then(function (viewState) {
            return $q.when(self.hasWritePermission(viewState[0].permission) && !self.packageExceptionApplies(viewState));
        });
    };

    self.hasPackageException = function (viewStateKey, pkgId) {
        return self.getPackagePermissions(viewStateKey, pkgId).then(function (viewState) {
            return $q.when(self.packageExceptionApplies(viewState));
        });
    };
});
